<template>
  <div class="search-fields sidebar__section" v-if="visible" :class="{'is-waypoints-dragged': isWaypointsDragged, 'is-collapse-enabled': collapseEnabled, 'is-collapsed': collapsed, 'is-collapsing': collapsing}" >
        <div class="search-fields__box" :style="{'height': boxHeight + 'px'}">
            <!-- Interim stop + public transport mode is active for mobile -->
            <InterimStopNote 
                v-if="mobile"
                :interimStopNote="interimStopMessageVisible && activeTransportModeNoIntermediateStops"
                @close="interimStopNoteCloseHandler"
            />
            <div class="search-fields__inner" ref="search-fields">
                <div 
                    class="search-fields__row" 
                    v-for="(waypoint, waypointIndex) in waypoints" 
                    :key="`${waypointIndex}-${waypointSortIndex}`"
                    :class="[
                        {'search-fields__row--toggle-top': collapseEnabled && waypointIndex === 0},
                        {'search-fields__row--toggle-bottom': collapseEnabled && waypointIndex === waypoints.length - 1}
                    ]"
                >
                    <Search 
                        ref="searchInput"
                        class="search-fields__row__field"
                        :placeholder="getSearchFieldPlaceholder(waypointIndex)" 
                        :field-icon="getSearchFieldIcon(waypointIndex)" 
                        :field-index="waypointIndex"
                        :value="waypoint.label"
                        :side-space="showSearchOrderOptions"
                        :disable-on-mobile="true"
                        :disabled="waypoint.disabled"
                        :move="waypoints.length <= 2 ? true : false"
                        :error="waypoint.wrongAddress"
                        @change="searchChangeHandler($event, waypointIndex)"
                        @click.native="searchFieldClickHandler($event, waypointIndex, waypoint.disabled)"
                        @keyup="searchTabHandler(waypointIndex)"
                    >
                        <icon slot="drag-handle" class="search-fields__row__drag-handle" :width="20" :height="20" view-box="0 0 20 20"><IconDragHandle /></icon>
                    </Search>
                    
                    <!-- Collapse toggle -->
                    <div 
                        class="search-fields__collapse-toggle" 
                        :class="{'is-bottom': waypointIndex === waypoints.length - 1}" 
                        v-if="showCollapseToggle(waypointIndex)"
                        @click="collapseToggleClickHandler"
                    >
                        <div class="search-fields__collapse-toggle__label copy"><span>
                            <template v-if="isCollapsedToggle(waypointIndex)">Zwischenziele ausblenden</template>
                            <template v-else>{{waypoints.length - 2}} Zwischenziele einblenden</template>
                        </span></div>
                        <div class="search-fields__collapse-toggle__icon">
                            <icon class="search-fields__collapse-toggle__icon__chevron" :width="20" :height="20" v-if="isCollapsedToggle(waypointIndex)"><IconChevronDown/></icon>
                            <icon v-else><IconExpand /></icon>
                        </div>
                    </div>

                    <!-- Add interim waypoint (control in between the search fields) -->
                    <transition name="fade">
                        <div class="search-fields__row__add-interim" :class="{'is-disabled': activeTransportModeNoIntermediateStops || (user && isWaypointLimitReached) || routesLoading}" v-if="showAddInterimBar(waypointIndex)" @click="addInterimClickHandler(waypointIndex + 1)">
                            <div class="search-fields__row__add-interim__icon">
                                <SmrTooltip content="Zwischenziel hinzufügen" position="bottom-end">
                                    <icon><IconPlusCircle /></icon>
                                </SmrTooltip> 
                            </div>
                        </div>   
                    </transition>
                                    
                    <!-- Reverse waypoints -->
                    <div class="search-fields__row__action sidebar__action-icon" v-if="waypointIndex === 0 && showSearchOrderOptions" @click="reverseClickHandler">
                        <SmrTooltip content="Richtung umkehren" position="right">
                            <icon><IconReverse /></icon>
                        </SmrTooltip>
                    </div>

                    <!-- Remove interim waypoint -->
                    <div class="search-fields__row__action sidebar__action-icon" :class="{'disabled' : routesLoading}" v-if="showRemoveInterimIcon(waypointIndex)" @click="removeInterimClickHandler(waypointIndex)">
                        <SmrTooltip content="Zwischenziel entfernen" position="right">
                            <icon><IconMinusCircle /></icon>
                        </SmrTooltip>    
                    </div>

                    <!-- Add interim waypoint -->
                    <div class="search-fields__row__action sidebar__action-icon" :class="{'disabled' : routesLoading}" v-if="showAddInterimIcon(waypointIndex)" @click="addInterimClickHandler(waypointIndex)">
                        <SmrTooltip content="Zwischenziel hinzufügen" position="right">
                            <icon><IconPlusCircle /></icon>
                        </SmrTooltip>    
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import eventBus from '@/utils/event-bus';
import safariBrowser from '@/utils/browser-type';
import { mapGetters } from 'vuex';
import Sortable from 'sortablejs';
import isEmpty from 'lodash/isEmpty'
import Search from '@/components/Search.vue'
import InterimStopNote from '@/components/InterimStopNote.vue'
import SmrTooltip from '@/components/SmrTooltip.vue'
import Icon from '@/components/Icon.vue'
import IconReverse from '@/components/icons/IconReverse.vue'
import IconPlusCircle from '@/components/icons/IconPlusCircle.vue'
import IconMinusCircle from '@/components/icons/IconMinusCircle.vue'
import IconDragHandle from '@components/icons/IconDragHandle.vue';
import IconChevronDown from '@components/icons/IconChevronDown.vue';
import IconExpand from '@components/icons/IconExpand.vue';
import { updateRouteUrl, queryWaypointsDifferent, decodeQueryParams, getWaypointsAsList } from '@/utils/url-utils'

export default {
    components: {
        Search,
        Icon,
        IconReverse,
        IconPlusCircle,
        IconMinusCircle,
        IconDragHandle,
        IconChevronDown,
        IconExpand,
        InterimStopNote,
        SmrTooltip,
    },
    props: {
        visible: {
            type: Boolean,
            default: true
        },
        waypoints: {
            type: Array,
            default: () => []
        },
        showSearchOrderOptions: {
            type: Boolean,
            default: false
        },
        isWaypointsDragged: {
            type: Boolean,
            default: false
        },
        waypointSortIndex: {
            type: Number,
            default: 0
        },
        collapseEnabled: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return  {
            collapsedHeight: 118,
            innerHeight: safariBrowser() ? 86 : 76,
            collapsed: false,
            collapsing: false,
            collapseDuration: 500,
            urlUpdatedManually: false,
            waypointsWatchTimeout: undefined,
            waypointsWatchUrlTimeout: undefined,
        }
    },
    computed: {
        ...mapGetters(['user', 'mobile', 'waypointsFromUrl', 'activeTransportModeTab', 'interimStopMessageVisible', 'isWaypointLimitReached', 'savedRouteInfo', 'routeWaypoints', 'activeTransportModeNoIntermediateStops', 'routesLoading', 'savedRouteTime', 'selectedPOIs']),
        boxHeight() {
            if(this.collapsed && this.collapseEnabled) {
               return this.collapsedHeight;
            } else {
                return this.innerHeight;
            }
        },
    },
    created() {
        this.addEventListeners();
        if(this.waypoints.length > 2) {
            requestAnimationFrame(this.updateHeight)
        }
    },
    mounted() {
        this.initSortable();
    },
    beforeDestroy() {
        this.removeEventListeners()
    },
    watch: {
        waypoints() {
            requestAnimationFrame(this.updateHeight)
            this.disableInterimStops();
            // reset the timouts when multiple waypoints are added fast
            if(this.waypointsWatchTimeout !== undefined) {
                clearTimeout(this.waypointsWatchTimeout)
                this.waypointsWatchTimeout = undefined
            }
            if(this.waypointsWatchUrlTimeout !== undefined) {
                clearTimeout(this.waypointsWatchUrlTimeout)
                this.waypointsWatchUrlTimeout = undefined
            }
            this.urlUpdatedManually = true
            this.waypointsWatchTimeout = setTimeout(() => {
                updateRouteUrl(this.$route, { waypoints: this.waypoints, routeTrafficTime: this.savedRouteTime,  pois: this.selectedPOIs })
                this.waypointsWatchUrlTimeout = setTimeout(() => {
                    this.urlUpdatedManually = false
                    this.waypointsWatchUrlTimeout = undefined
                }, 1000);
            }, 100);
        },
        savedRouteInfo() {
            if (!isEmpty(this.savedRouteInfo)) {
                this.setSavedWaypointsHandler()
            }
        },
        collapseEnabled(enabled) {
            requestAnimationFrame(this.updateHeight)
            if(enabled) {
                setTimeout(this.closeCollapse, 500);
            } else {
               this.collapsed = false;
               this.collapsing = false;
            }
        },
        activeTransportModeTab(val) {
            return val;
        }

    },
    methods: {
        addEventListeners() {
            eventBus.$on('closeCollapse', this.closeCollapse);
            eventBus.$on('showWaypointsFromUrl', this.showWaypointsFromUrlHandler)
            eventBus.$on('urlQueryChanged', this.urlQueryChangedHandler)
            eventBus.$on('focusOnSearchField', this.focusOnSearchFieldHandler)
        },
        removeEventListeners() {
            eventBus.$off('closeCollapse', this.closeCollapse);
            eventBus.$off('showWaypointsFromUrl', this.showWaypointsFromUrlHandler)
            eventBus.$off('urlQueryChanged', this.urlQueryChangedHandler)
            eventBus.$off('focusOnSearchField', this.focusOnSearchFieldHandler)
        },
        searchChangeHandler(waypoint, waypointIndex) {
            this.$emit('change', { waypoint, waypointIndex })
        },
        searchFieldClickHandler(e, waypointIndex, disabled) {
            if(!this.mobile) return false;
            // Do not show mobile search overlay for interim stops when public transport mode is active
            if(this.activeTransportModeNoIntermediateStops && disabled && 0 < waypointIndex < this.waypoints.length - 1) {
                return false;
            }
            // If using mobile view - show a search overlay instead of using this search field
            e.preventDefault();
            this.showMobileSearchOverlay(waypointIndex);
        },
        searchTabHandler(waypointIndex) {
            if (waypointIndex !== this.waypoints.length - 1) { // Stop at last waypoint
                this.$refs.searchInput[waypointIndex + 1].focusOnInput()
            }
        },
        showRemoveInterimIcon(waypointIndex) {
            if(waypointIndex > 0 && waypointIndex < this.waypoints.length - 1 && this.waypoints.length > 2) {
                return true;
            } else if(waypointIndex > 0 && this.activeTransportModeNoIntermediateStops && this.waypoints.length > 2) {
                return true;
            } else if (waypointIndex === this.waypoints.length - 1 && this.user && this.isWaypointLimitReached) {
                return true
            } else {
                return false;
            }
        },
        showAddInterimIcon(waypointIndex) {
            if (waypointIndex === this.waypoints.length - 1 && this.showSearchOrderOptions && !this.activeTransportModeNoIntermediateStops) {
                if (this.user && this.isWaypointLimitReached) {
                    return false
                } else {
                    return true
                }
            }
        },
        showAddInterimBar(waypointIndex) {
            if(this.collapseEnabled && this.collapsed) return false;
            return !this.mobile && waypointIndex !== this.waypoints.length - 1 && this.showSearchOrderOptions;
        },
        addInterimClickHandler(waypointIndex) {
            if(this.activeTransportModeNoIntermediateStops) return; // If mode public is active do not add interim stops
            if(this.routesLoading) return
            if(this.isWaypointLimitReached) { 
                if(!this.user) eventBus.$emit('showLoginMenu') // Show login panel if interim limit reached
                return
            }
            this.$store.commit('setInterimStopMessageVisible', true);
            this.openCollapse();
            this.$emit('addInterimClick', waypointIndex)
        },
        removeInterimClickHandler(waypointIndex) {
            if(this.routesLoading) return
            this.$emit('removeInterimClick', waypointIndex)
        },
        reverseClickHandler() {
            this.openCollapse();
            this.$emit('reverseClick');
        },
        interimStopNoteCloseHandler() {
            // this.interimStopMessage = false;
            // this.$store.commit('setInterimStopMessage', false);
            if(this.activeTransportModeNoIntermediateStops) {
                this.$store.commit('setInterimStopMessageVisible', false);
            }
        },
        collapseToggleClickHandler() {
            this.toggleCollapse();
        },
        showWaypointsFromUrlHandler() {
            if(this.waypointsFromUrl && this.waypointsFromUrl.length > 0) {
                this.setWaypointsFromUrl(this.waypointsFromUrl)
            }
        },
        urlQueryChangedHandler(e) {
            this.checkUrlWayoints(e)
        },
        setSavedWaypointsHandler() {
            this.urlUpdatedManually = true
            setTimeout(() => {
                this.urlUpdatedManually = false
            }, 1000);
        },
        focusOnSearchFieldHandler(waypointIndex) {
            if(this.collapseEnabled && this.collapsed) this.openCollapse()
            setTimeout(() => {
                if(this.mobile) {
                    this.showMobileSearchOverlay(waypointIndex);
                } else {
                    this.$refs.searchInput[waypointIndex].selectInput()
                }
            }, 100);
        },
        
        checkUrlWayoints(e) {
            if(!this.urlUpdatedManually) {
                if(queryWaypointsDifferent(e.from.query, e.to.query)) {
                    const queryData = decodeQueryParams(e.to.query)
                    const waypoints = getWaypointsAsList(queryData)
                    this.setWaypointsFromUrl(waypoints)
                }
            }
        },
        getSearchFieldPlaceholder(index) {
            if(index === 0) {
                return 'Start / Ortssuche';
            } else if (index === this.waypoints.length - 1) {
                return 'Ziel';
            } else {
                return 'Zwischenziel';
            }
        },
        getSearchFieldIcon(index) {
            if(this.waypoints[index].loadedIndex !== undefined) {
                if(this.waypoints[index].loadedIndex === 0) {
                    return 'IconSearchMarkStart';
                } else if (this.waypoints[index].loadedIndex === this.routeWaypoints.length - 1) {
                     return 'IconSearchMarkTarget';
                } else if(index === this.waypoints.length - 1) {
                    return 'IconSearchMarkInterim'; 
                } else {
                    return { showIndex: true, index: this.waypoints[index].loadedIndex }
                }
            } else {
                if(index === 0) {
                    return 'IconSearchMarkStart';
                } else if (index === this.waypoints.length - 1) {
                    return 'IconSearchMarkTarget';
                } else {
                    return 'IconSearchMarkInterim'; 
                }
            }
        },
        showMobileSearchOverlay(waypointIndex) {
            const searchValue = this.waypoints[waypointIndex].label;
            eventBus.$emit('showMobileSearch', {waypointIndex, searchValue});
        },
        initSortable() {
            let self = this;
            const searchFieldsEl = this.$refs['search-fields'];
            Sortable.create(searchFieldsEl, {
                animation: 150,
                handle: self.mobile ? false : '.search-fields__row__drag-handle',
                delayOnTouchOnly: true,
                delay: 200,
                touchStartThreshold: 20,
                onChoose() {
                    self.openCollapse();
                    self.$emit('waypointDraggedChange', true);
                    self.$emit('updateScrollPosition');
                },
                onUnchoose() {
                    self.$emit('waypointDraggedChange', false);
                },
                onEnd(e) {
                    self.$emit('optionMoved', {oldIndex: e.oldIndex, newIndex: e.newIndex});
                },
            });
        },
        updateHeight() {
            const searchFieldsEl = this.$refs['search-fields'];
            const height = searchFieldsEl.clientHeight;
            this.innerHeight = height;
        },
        toggleCollapse() {
            this.collapsed = !this.collapsed;
            this.collapsing = true;
            setTimeout(() => {
                this.collapsing = false;
            }, this.collapseDuration);
        },
        openCollapse() {
            if(this.collapseEnabled && this.collapsed) {
                this.toggleCollapse();
            }
        },
        closeCollapse() {
            if(this.collapseEnabled && !this.collapsed) {
                this.toggleCollapse();
            }
        },
        showCollapseToggle(waypointIndex) {
            if(!this.collapseEnabled) return false;
            if(this.collapsed) {
                return  waypointIndex === 0;
            } else {
                return waypointIndex === 0 || waypointIndex === this.waypoints.length - 1;
            }
        },
        isCollapsedToggle(waypointIndex) {
            return (waypointIndex === 0 && !this.collapsed) || waypointIndex !== 0;
        },
        setWaypointsFromUrl(waypoints) {
            // check for interim waypoints and add the searchfields
            const interimWaypoints = waypoints.length > 2 ? waypoints.slice(1, waypoints.length - 1) : false
            if(interimWaypoints) {
                for(let i = 0; i < interimWaypoints.length; i++) {
                    this.openCollapse()
                    this.$emit('addInterimClick', 1)
                }
                this.$store.commit('setInterimStopsFromUrl', true)
            }
            setTimeout(() => {
                waypoints.forEach((waypoint, index) => {
                    waypoint.highlight = waypoint.name
                    if(this.$refs.searchInput[index]) {
                        this.$refs.searchInput[index].autoSetResult(waypoint)
                    }
                })
            }, 100);
            this.$store.commit('setWaypointsFromUrl', false)
        },
        // Disable interim stops for public transport mode
        disableInterimStops() {
            if(this.waypoints.length) {
                this.waypoints.forEach((waypoint,index) => {
                    if(index > 0 && index < this.waypoints.length - 1) {
                        Object.assign(waypoint, {disabled: true});
                    } 
                    
                    if(index === 0 || index === this.waypoints.length - 1) {
                        Object.assign(waypoint, {disabled: false });
                    }
                });
            }
        },
    }
}
</script>

<style lang="scss">
$collapse-duration: 0.5s;

.search-fields {
    $block: &;
    margin-bottom: 10px;
    margin-top: 6px;
    position: relative;
    z-index: 3;
    user-select: none;
    &__box {
        position: relative;
        .is-collapsing & {
            overflow: hidden;
            transition: height $collapse-duration $ease-out-quart;
        }
    }
    &__inner {
        .is-collapse-enabled &{
            padding-bottom: 76px;
        }
        .is-collapsed & {
            pointer-events: none;
        }
    }
    &__row {
        position: relative;
        &:not(:last-child) {
            margin-bottom: 8px;
        }
        .is-collapsed:not(.is-collapsing) &:not(:first-child):not(:last-child) {
            visibility: hidden;
            pointer-events: none;
        }
        .is-collapsed & {
            &:first-child,
            &:last-child {
                pointer-events: auto;
            }
        }
        &.sortable-ghost {
            opacity: 0.3;
        }
        .is-collapse-enabled & {
            &:first-child {
                margin-bottom: 50px;
                #{$block}__row__add-interim {
                    margin-top: 42px;
                }
            }
            &:last-child {
                margin-top: 50px;
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                background-color: white;
            }
        }
        &__field {
            flex-grow: 1;
        }
        &__action {
            transition: opacity 0.15s;
            flex-shrink: 0;
            margin-left: 16px;
            position: absolute;
            top: 50%;
            right: 0;
            transform: translateY(-50%);
            z-index: 5;
            // .is-waypoints-dragged & {
            //     opacity: 0;
            // }
        }
        .is-waypoints-dragged &:not(.sortable-chosen) {
            #{$block}__row__drag-handle {
                @include mq(desktop) {
                    display: none;
                }
            }
        }
        &__drag-handle {
            cursor: pointer;
            cursor: grab;
            .is-waypoints-dragged &{
                cursor: grabbing;
            }
        }
        &__add-interim {
            transition: opacity 0.3s;
            position: absolute;
            top: 100%;
            left: 0;
            right: 40px;
            height: 8px;
            opacity: 0;
            z-index: 3;
            &.is-disabled {
                &::before {
                    background: $color-grey-silver;
                }
                #{$block}__row__add-interim__icon {
                    color: $color-grey-silver;
                    .no-touch &:hover {
                        color: $color-grey-silver;
                    }
                }
            }
            @media (hover: hover) {
                &:hover {
                    opacity: 1;
                    transition-delay: 0.15s;
                    #{$block}__row__add-interim__icon {
                        transition-delay: 0.15s;
                        transform: translate(0, -50%);
                        pointer-events: auto;
                    }
                }
            }
            &::before {
                content: '';
                position: absolute;
                top: 50%;
                left: 0;
                width: 100%;
                height: 2px;
                margin-top: -1px;
                background-color: $color-grey-dark;
            }
            &__icon {
                transition: transform 0.3s $ease-out-quart;
                position: absolute;
                top: 50%;
                right: 25%;
                transform: translate(5px, -50%);
                width: 18px;
                height: 18px;
                color: $color-grey-dark;
                pointer-events: none;
                cursor: pointer;
                .no-touch &:hover {
                    color: $color-black;
                }
                &::before {
                    content: '';
                    position: absolute;
                    top: 50%;
                    left: -3px;
                    right: -3px;
                    height: 4px;
                    margin-top: -2px;
                    background-color: white;
                }
                &::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    border-radius: 100%;
                    background-color: white;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
                }
                svg {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    z-index: 2;
                }
            }
        }
    }
    &__collapse-toggle {
        transition: 0.2s opacity;
        display: flex;
        align-items: center;
        height: 34px;
        position: absolute;
        top: 100%;
        left: 0;
        width: 100%;
        margin-top: 8px;
        cursor: pointer;
        user-select: none;
        .no-touch &:hover {
            #{$block}__collapse-toggle {
                &__label {
                    opacity: 1;
                }
                &__icon {
                    color: $color-black;
                }
            }
        }
        .is-waypoints-dragged &{
            opacity: 0;
        }
        &.is-bottom {
            top: auto;
            bottom: 100%;
            margin-top: 0;
            margin-bottom: 8px;
            #{$block}__collapse-toggle__icon svg{
                transform: rotate(180deg);
            }

            .is-collapsed & {
                opacity: 0;
            }
            .is-collapsing & {
                transition: none;
                animation: showToggle 0.3s 0.2s both;
            }
            .is-collapsed.is-collapsing & {
                animation: hideToggle 0.3s both;
            }
        }
        &__label {
            transition: 0.2s opacity;
            flex-grow: 1;
            position: relative;
            opacity: 0.4;
            display: flex;
            justify-content: center;
            &::before {
                content: '';
                top: 50%;
                left: 0;
                width: 100%;
                height: 2px;
                margin-top: -1px;
                background-color: $color-black;
                position: absolute;
                opacity: 0.5;
            }
            span {
                position: relative;
                background-color: white;
                padding: 0 1em;
            }
        }
        &__icon {
            transition: 0.2s color;
            width: 40px;
            text-align: right;
            color: $color-grey-dark;
            line-height: 1;
            &__chevron {
                margin-right: 2px;
            }
        }
    }
}

@keyframes hideToggle {
    from {
        opacity: 1;
    }
    to {
        opacity: 0;
    }
}

@keyframes showToggle {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}
</style>
